import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  DateRangePicker,
  DateRangePickerProps,
  Link,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from '@amzn/awsui-components-react';
import React, { memo, MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { paginationLabels } from '@/src/utils/table.utils';
import { TableEmptyState } from '@/src/components/common/TableEmptyState';
import { TableHeader } from '@/src/components/common/TableHeader';
import { useApi } from '@/src/store/api.context';
import { EVENT_DETAILS_ROUTES } from '@/src/routes';
import { AllEventsReportItem } from '@/src/types/AllEventsReportItem';
import { createPortal } from 'react-dom';
import { downloadAsJson } from '@/src/utils/download.utils';
import { omit } from 'lodash';
import StarRating from '@/src/components/common/StarRating';
import { getLanguageCodeSafe } from '@/src/utils/locale.utils';

const MultiLineTableHeader: React.FC<{ label: string; stat?: string | number | null }> = memo(({ label, stat }) => {
  const shouldDisplayStat = !(stat === '' || stat === null || stat === undefined || Number.isNaN(stat));
  if (!shouldDisplayStat) {
    return <>{label}</>;
  }
  return (
    <SpaceBetween direction="vertical" size="xxxs">
      <Box variant="h4" fontWeight="bold">
        {typeof stat === 'number' && !Number.isInteger(stat) ? stat.toFixed(2) : stat}
      </Box>
      <Box color="text-label" fontSize={!!stat ? 'body-s' : 'body-m'}>
        {label}
      </Box>
    </SpaceBetween>
  );
});

const AllEventsReportContainer: React.FC<{ exportBtnRef?: MutableRefObject<HTMLElement | null> }> = ({
  exportBtnRef,
}) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<
    { reportRows: AllEventsReportItem[]; totalResults?: number; generatedAt: number } | undefined
  >();
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchText, setSearchText] = useState('');
  const { reportsApi } = useApi();

  const startDate = (dateRange as DateRangePickerProps.AbsoluteValue)?.startDate;
  const endDate = (dateRange as DateRangePickerProps.AbsoluteValue)?.endDate;

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 20,
    contentDensity: 'compact',
    stripedRows: true,
  });

  const numberOfPages = useMemo(() => {
    if (!data?.totalResults) return 0;
    return Math.ceil(data?.totalResults / (preferences.pageSize || 20));
  }, [data]);

  useEffect(() => {
    if (startDate) {
      void loadAllEvents(startDate, endDate);
    }
  }, [preferences.pageSize, pageNumber]);

  const filteringFunction = (item: AllEventsReportItem, filteringText: string) => {
    const searchableText = [item.eventTitle, item.eventName].filter(Boolean).join(' ').toLowerCase();
    return searchableText.includes(filteringText.toLowerCase());
  };

  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    data?.reportRows || [],
    {
      filtering: {
        filteringFunction,
        empty: <TableEmptyState title={t(i18nKeys.events.list.empty.title)} />,
        noMatch: (
          <TableEmptyState
            title={t(i18nKeys.tables.noMatch.title)}
            subtitle={t(i18nKeys.tables.noMatch.subtitle)}
            onClearFilter={() => actions.setFiltering('')}
          />
        ),
      },
      pagination: { pageSize: preferences.pageSize },
      sorting: {},
    }
  );

  const aggregateStats = useMemo(() => {
    if (!items?.length || loading) {
      return null;
    }
    const summed = items.reduce(
      (accum, val) => {
        accum.totalFacilitators += val.totalFacilitators;
        accum.totalParticipants += val.totalParticipants;
        accum.totalTeams += val.totalTeams;
        accum.numberOfChallenges += val.numberOfChallenges;
        accum.numTeamsStarted += val.numTeamsStarted;
        accum.numTeamsAttempted += val.numTeamsAttempted;
        accum.numTeamsSolved += val.numTeamsSolved;
        accum.cluesRequested += val.cluesRequested;
        accum.avgEventRank += val.avgEventRank;
        accum.eventRankDataPoints += val.avgEventRank > 0 ? 1 : 0;
        accum.avgChallengeRank += val.avgChallengeRank;
        accum.challengeRankDataPoints += val.avgChallengeRank > 0 ? 1 : 0;
        accum.numLearned += val.numLearned;
        accum.numSubmittedFeedback += val.numSubmittedFeedback;
        return accum;
      },
      {
        totalFacilitators: 0,
        totalParticipants: 0,
        totalTeams: 0,
        numberOfChallenges: 0,
        numTeamsStarted: 0,
        numTeamsAttempted: 0,
        numTeamsSolved: 0,
        cluesRequested: 0,
        avgEventRank: 0,
        eventRankDataPoints: 0,
        avgChallengeRank: 0,
        challengeRankDataPoints: 0,
        numLearned: 0,
        numSubmittedFeedback: 0,
      }
    );
    if (summed.avgChallengeRank) {
      summed.avgChallengeRank /= summed.challengeRankDataPoints;
    }
    if (summed.avgEventRank) {
      summed.avgEventRank /= summed.eventRankDataPoints;
    }
    return summed;
  }, [items, loading]);

  const displayEventDate = (startDate: string, endDate: string) => {
    const toLocaleDate = (dateString: string) =>
      new Date(dateString).toLocaleString(getLanguageCodeSafe(i18n?.language), {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      });
    const displayStartDate = toLocaleDate(startDate);
    const displayEndDate = toLocaleDate(endDate);
    if (displayStartDate === displayEndDate) {
      return <>{displayStartDate}</>;
    }
    return (
      <>
        {displayStartDate} - {displayEndDate}
      </>
    );
  };

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'title',
        header: t(i18nKeys.tables.tableConfig.title),
        headerName: t(i18nKeys.tables.tableConfig.title),
        cell: (item: AllEventsReportItem) =>
          item.eventTitle ? (
            <Link href={EVENT_DETAILS_ROUTES.Summary.resolve(item?.eventName ?? '')} external>
              {item.eventTitle}
            </Link>
          ) : (
            '-'
          ),
        sortingField: 'eventTitle',
        alwaysVisible: true,
      },
      {
        id: 'date',
        header: t(i18nKeys.challenges.challengeDetails.chart.feedback.xAxisTitle),
        headerName: t(i18nKeys.challenges.challengeDetails.chart.feedback.xAxisTitle),
        cell: (item: AllEventsReportItem) => displayEventDate(item.eventStartDate, item.eventEndDate),
        sortingField: 'eventStartDate',
      },
      {
        id: 'facilitators',
        header: (
          <MultiLineTableHeader
            label={t(i18nKeys.eventLabs.metrics.infoSections.facilitators)}
            stat={aggregateStats?.totalFacilitators}
          />
        ),
        headerName: t(i18nKeys.eventLabs.metrics.infoSections.facilitators),
        cell: (item: AllEventsReportItem) => item.totalFacilitators,
        sortingField: 'totalFacilitators',
      },
      {
        id: 'participants',
        header: (
          <MultiLineTableHeader
            label={t(i18nKeys.eventLabs.metrics.infoSections.participants)}
            stat={aggregateStats?.totalParticipants}
          />
        ),
        headerName: t(i18nKeys.eventLabs.metrics.infoSections.participants),
        cell: (item: AllEventsReportItem) => item.totalParticipants,
        sortingField: 'totalParticipants',
      },
      {
        id: 'teams',
        header: (
          <MultiLineTableHeader
            label={t(i18nKeys.eventReport.challengeTable.teams)}
            stat={aggregateStats?.totalTeams}
          />
        ),
        headerName: t(i18nKeys.eventReport.challengeTable.teams),
        cell: (item: AllEventsReportItem) => item.totalTeams,
        sortingField: 'totalTeams',
      },
      {
        id: 'numberOfChallenges',
        header: (
          <MultiLineTableHeader
            label={t(i18nKeys.tables.tableConfig.challenges)}
            stat={aggregateStats?.numberOfChallenges}
          />
        ),
        headerName: t(i18nKeys.tables.tableConfig.challenges),
        cell: (item: AllEventsReportItem) => item.numberOfChallenges,
        sortingField: 'numberOfChallenges',
      },
      {
        id: 'challengeStats',
        header: (
          <MultiLineTableHeader
            label="Challenges Started/Attempted/Solved"
            stat={
              aggregateStats &&
              `${aggregateStats.numTeamsStarted}/${aggregateStats.numTeamsAttempted}/${aggregateStats.numTeamsSolved}`
            }
          />
        ),
        headerName: 'Challenges Started/Attempted/Solved',
        cell: ({ numTeamsStarted, numTeamsAttempted, numTeamsSolved }: AllEventsReportItem) =>
          `${numTeamsStarted}/${numTeamsAttempted}/${numTeamsSolved}`,
      },
      {
        id: 'cluesRequested',
        header: (
          <MultiLineTableHeader label={t(i18nKeys.eventReport.cluesUsed)} stat={aggregateStats?.cluesRequested} />
        ),
        headerName: t(i18nKeys.eventReport.cluesUsed),
        cell: (item: AllEventsReportItem) => item.cluesRequested,
        sortingField: 'cluesRequested',
      },
      {
        id: 'avgEventRank',
        header: (
          <MultiLineTableHeader label={t(i18nKeys.feedback.labels.eventRating)} stat={aggregateStats?.avgEventRank} />
        ),
        headerName: t(i18nKeys.feedback.labels.eventRating),
        cell: (item: AllEventsReportItem) => <StarRating value={item.avgEventRank} showAverage="short" />,
        sortingField: 'avgEventRank',
      },
      {
        id: 'avgChallengeRank',
        header: (
          <MultiLineTableHeader
            label={t(i18nKeys.feedback.headers.challengeRating)}
            stat={aggregateStats?.avgChallengeRank}
          />
        ),
        headerName: t(i18nKeys.feedback.headers.challengeRating),
        cell: (item: AllEventsReportItem) => <StarRating value={item.avgChallengeRank} showAverage="short" />,
        sortingField: 'avgChallengeRank',
      },
      {
        id: 'numLearned',
        header: (
          <MultiLineTableHeader
            label={t(i18nKeys.reports.participantslearned)}
            stat={aggregateStats ? `${aggregateStats.numLearned}/${aggregateStats.numSubmittedFeedback}` : null}
          />
        ),
        headerName: t(i18nKeys.reports.participantslearned),
        cell: (item: AllEventsReportItem) => (
          <>
            {item.numLearned}/{item.numSubmittedFeedback}
          </>
        ),
      },
    ],
    [items, aggregateStats, loading]
  );

  const loadAllEvents = async (dateRangeStart: string, dateRangeEnd?: string) => {
    setLoading(true);
    try {
      setData(
        await reportsApi.getAllEventsReport(
          dateRangeStart,
          dateRangeEnd,
          pageNumber,
          preferences.pageSize,
          filterProps.filteringText
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const submitSearch = () => {
    if (startDate) {
      void loadAllEvents(startDate, endDate);
    } else {
      alert('cannot search without a start date');
    }
  };

  return (
    <Table
      {...collectionProps}
      resizableColumns
      data-testid="reports__all-events-table"
      loading={loading}
      header={
        <>
          {!!exportBtnRef?.current &&
            createPortal(
              <Button
                iconName="download"
                disabled={!data?.reportRows?.length}
                data-testid="export-report-to-json-button"
                onClick={() => {
                  downloadAsJson(
                    JSON.stringify(data?.reportRows, null, 2),
                    `all-events-${startDate}--${endDate}--page-${pageNumber}-of-${numberOfPages}`
                  );
                }}
              >
                {t(i18nKeys.reports.exportToJson)}
              </Button>,
              exportBtnRef.current
            )}

          <TableHeader totalItems={data?.totalResults} title={t(i18nKeys.reports.allEvents)} />
        </>
      }
      columnDefinitions={columnDefinitions.map((cd) => omit(cd, 'alwaysVisible'))}
      items={items}
      pagination={
        <Pagination
          {...paginationProps}
          pagesCount={numberOfPages}
          ariaLabels={paginationLabels(t)}
          currentPageIndex={pageNumber}
          onChange={({ detail }) => setPageNumber(detail.currentPageIndex)}
        />
      }
      preferences={
        <CollectionPreferences
          title={t(i18nKeys.general.preferences.title)}
          confirmLabel={t(i18nKeys.general.confirm)}
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          cancelLabel={t(i18nKeys.general.cancel)}
          pageSizePreference={{
            options: [
              { value: 10, label: '10' },
              { value: 20, label: '20' },
              { value: 50, label: '50' },
              { value: 100, label: '100' },
            ],
          }}
          stripedRowsPreference={{}}
          contentDensityPreference={{}}
          contentDisplayPreference={{
            options: columnDefinitions.map((cd) => ({
              id: cd.id,
              label: cd.headerName,
              alwaysVisible: cd.alwaysVisible,
            })),
          }}
        />
      }
      columnDisplay={preferences.contentDisplay}
      contentDensity={preferences.contentDensity}
      stripedRows={preferences.stripedRows}
      wrapLines
      variant="full-page"
      filter={
        <SpaceBetween direction="horizontal" size="m">
          <TextFilter
            filteringPlaceholder={t(i18nKeys.events.searchEvents)}
            filteringText={searchText}
            onChange={({ detail }) => setSearchText(detail.filteringText)}
            countText={t(i18nKeys.tables.matchesCount, { count: filteredItemsCount })}
            filteringAriaLabel={t(i18nKeys.events.filteringLabel)}
          />
          <DateRangePicker
            onChange={({ detail }) => setDateRange(detail.value)}
            dateOnly
            rangeSelectorMode="absolute-only"
            value={dateRange}
            isValidRange={() => ({ valid: true })}
            relativeOptions={[]}
          />
          <Button disabled={!startDate} loading={loading} onClick={() => submitSearch()}>
            {t(i18nKeys.settings.jamUILocalization.labels.noItemsSearch)}
          </Button>
        </SpaceBetween>
      }
    />
  );
};

export default AllEventsReportContainer;
